// ==== Date Range Selector Dropdown Funcs ====

const getPresetDateRanges = (excludedPresets = [], includeToday = false) => {
  const presets = {
    // today: {
    //   name: 'Today',
    //   start: floorDate(new Date(new Date().setDate(new Date().getDate() - 1))), 
    //   end: new Date()
    // },
    last7Days: {
      name: 'Last 7 days',
      start: floorDate(new Date(new Date().setDate(new Date().getDate() - 7 + (includeToday ? 1 : 0)))), 
      end: includeToday ? new Date() : getEndOfYesterday()
    },
    last14Days: {
      name: 'Last 14 days',
      start: floorDate(new Date(new Date().setDate(new Date().getDate() - 14 + (includeToday ? 1 : 0)))), 
      end: includeToday ? new Date() : getEndOfYesterday()
    },
    last30Days: {
      name: 'Last 30 days',
      start: floorDate(new Date(new Date().setDate(new Date().getDate() - 30 + (includeToday ? 1 : 0)))), 
      end: includeToday ? new Date() : getEndOfYesterday()
    },
    last90Days: {
      name: 'Last 90 days',
      start: floorDate(new Date(new Date().setDate(new Date().getDate() - 90 + (includeToday ? 1 : 0)))), 
      end: includeToday ? new Date() : getEndOfYesterday()
    },
    last365Days: {
      name: 'Last 365 days',
      start: floorDate(new Date(new Date().setDate(new Date().getDate() - 365 + (includeToday ? 1 : 0)))), 
      end: includeToday ? new Date() : getEndOfYesterday()
    },
    allTime: {
      name: 'All time',
      start: null,
      end: null
    }
  }
  
  // Filter out any excluded presets
  if (excludedPresets.length > 0) {
    return Object.fromEntries(
      Object.entries(presets).filter(([key]) => !excludedPresets.includes(key))
    );
  }
  
  return presets;
}

const getSingleDatePresets = () => ({
  today: { name: 'Today', date: floorDate(new Date()) },
  sevenDaysAgo: { name: '7 days ago', date: floorDate(new Date(new Date().setDate(new Date().getDate() - 7))) },
  fourteenDaysAgo: { name: '14 days ago', date: floorDate(new Date(new Date().setDate(new Date().getDate() - 14))) },
  thirtyDaysAgo: { name: '30 days ago', date: floorDate(new Date(new Date().setDate(new Date().getDate() - 30))) },
  sixtyDaysAgo: { name: '60 days ago', date: floorDate(new Date(new Date().setDate(new Date().getDate() - 60))) },
  ninetyDaysAgo: { name: '90 days ago', date: floorDate(new Date(new Date().setDate(new Date().getDate() - 90))) }
})

const areDatesEqual = (a, b) => !!a && !!b &&
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()

const getMatchingPresetName = (dateRange) => {
  const presetDateRanges = getPresetDateRanges()
  for (const [_, range] of Object.entries(presetDateRanges)) {
    if (areDateRangesEqual(range, dateRange)) {
      return range.name
    }
  }
  return null
}

const areDateRangesEqual = (range1, range2) => {
  if (!range1 || !range2 || !range1.start || !range2.start || !range1.end || !range2.end) return false
  return (
    range1.start.getFullYear() === range2.start.getFullYear() &&
    range1.start.getMonth() === range2.start.getMonth() &&
    range1.start.getDate() === range2.start.getDate() &&
    range1.end.getFullYear() === range2.end.getFullYear() &&
    range1.end.getMonth() === range2.end.getMonth() &&
    range1.end.getDate() === range2.end.getDate()
  )
}

const floorDate = (date) => {
  // Decrease the date to the first possible second of the day
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

// Convert a stored share-report date into a timezone-stable calendar Date.
// YYYY-MM-DD is treated as a calendar day (not UTC midnight). ISO timestamps
// produced by JSON.stringify(floorDate/ceilDate) are mapped back to the
// intended calendar day so every viewer sees the same label.
const toCalendarDate = (value, bound = 'start') => {
  if (value == null || value === '') return null

  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    const [year, month, day] = value.trim().split('-').map(Number)
    return new Date(year, month - 1, day)
  }

  const date = value instanceof Date ? value : new Date(value)
  if (isNaN(date.getTime())) return null

  const isLocalCalendarMidnight = (
    date.getHours() === 0 &&
    date.getMinutes() === 0 &&
    date.getSeconds() === 0 &&
    date.getMilliseconds() === 0
  )
  if (isLocalCalendarMidnight) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate())
  }

  const utcHours = date.getUTCHours()
  let year = date.getUTCFullYear()
  let month = date.getUTCMonth()
  let day = date.getUTCDate()

  if (bound === 'end') {
    // 23:59 local in western timezones serializes to the next UTC morning
    if (utcHours < 12) {
      const shifted = new Date(Date.UTC(year, month, day - 1))
      year = shifted.getUTCFullYear()
      month = shifted.getUTCMonth()
      day = shifted.getUTCDate()
    }
  } else if (utcHours >= 12) {
    // local midnight in eastern timezones serializes to the previous UTC afternoon
    const shifted = new Date(Date.UTC(year, month, day + 1))
    year = shifted.getUTCFullYear()
    month = shifted.getUTCMonth()
    day = shifted.getUTCDate()
  }

  return new Date(year, month, day)
}

const ceilDate = (date) => {
  // Increase the date to the last possible second of the day
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999)
}

const getEndOfYesterday = () => {
  const yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1) // Move to yesterday
  yesterday.setHours(23, 59, 59, 999) // Set to last millisecond of the day
  return yesterday
}

const formatCustomDateRangeLabel = (startDate, endDate) => {
  // Check if the start and end dates are in the same year.
  if (startDate.getFullYear() === endDate.getFullYear()) {
    // Format: "Jan 27 - Feb 9, 2025"
    const startStr = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endStr = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${startStr} - ${endStr}`;
  } else {
    // Format: "Jan 27, 2024 - Feb 9, 2025"
    const startStr = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const endStr = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${startStr} - ${endStr}`;
  }
}

const getTimeAgoShorthand = (date) => {
  const now = new Date()
  const diffMs = now - date
      
  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHour = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHour / 24)
  
  if (diffMin < 60) {
    return `${diffMin}m`
  } else if (diffHour < 24) {
    return `${diffHour}h`
  } else {
    return `${diffDay}d`
  }
}

const isWithinDaysAgo = (date, days) => {
  const now = new Date()
  const diffMs = now - date
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  return diffDays <= days
}

const getAdEffectiveEndDate = (ad) => {
  if (ad?.live) return +new Date()
  if (ad?.end_date && !isNaN(ad.end_date)) return ad.end_date
  const lastChecked = ad?.lastChecked || ad?.last_checked
  if (lastChecked?._seconds) return lastChecked._seconds * 1000
  if (lastChecked?.seconds) return lastChecked.seconds * 1000
  return +new Date()
}

export { 
  getPresetDateRanges, 
  getSingleDatePresets,
  areDateRangesEqual, 
  areDatesEqual,
  getMatchingPresetName, 
  formatCustomDateRangeLabel, 
  floorDate, 
  ceilDate,
  getEndOfYesterday,
  getTimeAgoShorthand,
  isWithinDaysAgo,
  getAdEffectiveEndDate,
  toCalendarDate
}